import { Injectable } from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';

import { Store } from '@ngrx/store';
import * as _ from 'lodash';
import {BehaviorSubject, combineLatest, forkJoin, Observable, of, throwError} from 'rxjs';
import { BaseService } from '../../api/base-service';
import { ApiConfiguration } from '../../api/api-configuration';
import { RootState } from '../../app.module';
import { OptionList, OptionValue } from '../../utils/options';
import { DAY_RANGES } from '../../components/constants/dayRanges';
import { TwoMinutes } from '../../utils/time-utils';
import { environment } from 'environments/environment';
import {IUserProfile} from "@zonar-ui/auth/lib/models/user-profile.model";
import {PermissionsService} from "@zonar-ui/auth";
import {Division} from "@zonar-ui/sidenav/lib/models/company.model";
import {catchError, filter, map, switchMap, take} from "rxjs/operators";
import {LocalStorageService} from "../../local-storage.service";

export const REFRESH_TIME_IN_MS = 120000;

@Injectable({
	providedIn: 'root',
})
export class AppService extends BaseService {
	public interval: any;
	public isAppDataLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private _updatedAccountList$: BehaviorSubject<{title: string, value: any}[]> =
		new BehaviorSubject<{title: string, value: any}[]>([]);

	constructor(
		public store: Store<RootState>,
		public permissionsService: PermissionsService,
		config: ApiConfiguration,
		http?: HttpClient,
		public localStorageService?: LocalStorageService,
	) {
		super(config, http);
	}

	getAccountList(): Observable<{title: string, value: any}[]> {
		return this._updatedAccountList$;
	}

	setAccountList(list: {title: string, value: any}[]) {
		this._updatedAccountList$.next(list);
	}

	loadDispatchers(): Observable<Object> {
		return this.http.get(`${this.rootUrl}/api/v2/dispatchers`, {});
	}
	//
	// loadAssetOptions(): Observable<Object> {
	// 	const accountCode = this.localStorageService.get('activeAccount');
	// 	return this.http.get(`${environment.urls.legacyEntityApi}/api/v1/assets`,
	// 		{params: { account_code: accountCode}});
	// }

	loadLocationOptions(account?: string): Observable<Object> {
		if (!account) {
			account = this.localStorageService.get('activeAccount');
		}

		const params = {
			accountCode: account
		};
		return this.http.get(`${environment.urls.udlApi}/locations/`, {params}).pipe(
			map((results) => {
				return {
					locations: results,
					account: account
				};
			})
		);
	}

	// loadDriverOptions(): Observable<Object> {
	// 	const accountCode = this.localStorageService.get('activeAccount');
	// 	return this.http.get(`${environment.urls.legacyEntityApi}/api/v1/drivers`,
	// 		{params: { account_code: accountCode}});
	// }

	loadUserData(): Observable<Object> {
		return this.http.get(`${this.rootUrl}/api/v1/userDetails`, {});
	}

	getContextualHelp(id: string, helpType: string): Observable<Object> {
		return this.http.get(`${this.rootUrl}/api/v2/contextual-help`, {
			params: { id, helpType },
		});
	}

	refreshData(actions = []): void {
		const self = this;
		clearInterval(this.interval);
		this.interval = setInterval(() => {
			_.forEach(actions, (action) => {
				self.store.dispatch(action);
			});
		}, TwoMinutes);
	}

	refreshDataOnce(actions = []): void {
		_.forEach(actions, (action) => {
			this.store.dispatch(action);
		});
	}

	stopRefreshing(): void {
		clearInterval(this.interval);
	}

	// TODO: to get options from shared service
	getTimeOptions(timeOption = 'day', selectedOption: OptionValue | {}): OptionList {
		const timeFilter = {
			selectedOption: {},
			options: [],
		};
		if (timeOption === 'day') {
			timeFilter.selectedOption = selectedOption || DAY_RANGES[5].value; // defaults to 180 days
			timeFilter.options = DAY_RANGES;
		}
		return timeFilter;
	}

	public isZonarAdmin(user: any): boolean {
		return user.email.endsWith('@zonarsystems.com');
	}

	public isCustomerAdmin(account: string): boolean {
		return environment.accountsWithAdmin.includes(account);
	}

	getDispatcherIdByEmail(email: string): Observable<{id: any}> {
		// Entity API get dispatcher is not working due to zonar owner id is not attaching on header
		// TODO: figure out why the owner id is not attached on header by the interceptor
		// const url = `${environment.urls.externalApi}/dispatchers`;
		// return this.http.get(url, { params: { userId } })
		// 	.pipe(map(dispatchers => dispatchers[0]));
		console.log('email ', email);
		email = email.includes('+') ? email.replace('+', '%2B'): email;
		// return this.http.get<{id: any}>(`${this.rootUrl}/api/v2/legacyDispatcherId?email=${email}`);
		const accountCode = this.localStorageService.get('activeAccount');
		const url = `${environment.urls.legacyEntityApi}/api/v1/dispatchers`;
		return this.http.get(url, {params: { account_code: accountCode, email}}).pipe(
			map((result) => {
				//dispatcher list can return as empty here
				return result[0];
			})
		) as any;
	}

	getDispatchers():Observable<any> {
		const accountCode = this.localStorageService.get('activeAccount');
		const url = `${environment.urls.legacyEntityApi}/api/v1/dispatchers`;
		return this.http.get(url, {params: { account_code: accountCode}}).pipe(
			map((results) => {
				return {
					dispatchers: results,
					account: accountCode
				};
			})
		);
	}

	getCompanyByLegacyAccount(accountCode?): Observable<any[]> {
		if (!accountCode) {
			return of(null);
		}
		const params = {legacyAccountCode: accountCode};
		return this.http.get<string[]>(`${environment.urls.externalApi}/companies`, {params});
	}

	getCompanyLegacyTypeDivisions(companyId: string): Observable<Division[]> {
		return this.http.get<Division[]>(`${environment.urls.externalApi}` +
			`/companies/${companyId}/divisions?type=LEGACY&status=ACTIVE&sort=legacyAccountCode:asc`);
	}

	getCompanyAccountsById(companyId: string): Observable<{value: string}[]> {
		return this.getCompanyLegacyTypeDivisions(companyId)
			.pipe(
				map(divisions => [...new Set((divisions || []).map(div => div.legacyAccountCode))]),
				map(accounts => accounts.map(acc => ({ value: acc }))));
	}

	setCompanyInSidenav(companyId: string, userProfile?: IUserProfile) {
		return combineLatest([
			this.getCompanyLegacyTypeDivisions(companyId)
		]).pipe(
			filter(x=> !!x),
			switchMap(([accounts]) => {
				// setting the legacy accounts for filtering
				let accountList: {title: string, value: any}[] = accounts.map(acc => ({title: acc.legacyAccountCode, value: acc.legacyAccountCode}));
				accountList = accountList.filter(acc => !environment.excludeAccounts.includes(acc.value));
				this.setAccountList(accountList);
				let selectedAcct = accountList[0].value;
				this.permissionsService.setCurrentCompanyContextById(companyId);
				return of([selectedAcct, accountList]);
			}),
			catchError(err => {
				const errorText = `Failed to getLegacyAccount codes while setting company: ${err.message}`;
				return throwError(errorText);
			})
		)
	}
}
